import type { Recipe } from "@/types/recipe"
import type { Review } from "@/types/review"

import { siteConfig } from "@/data/site"
import { buildBreadcrumbSchema, buildRecipeSchema } from "@/lib/schema"
import { absoluteUrl } from "@/lib/utils"

type RecipeSchemaProps = {
  recipe: Recipe
  reviews: Review[]
  ratingSummary: {
    average: number
    count: number
  }
}

function serializeJsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, "\\u003c")
}

export function RecipeSchema({
  recipe,
  reviews,
  ratingSummary,
}: RecipeSchemaProps) {
  const recipeUrl = absoluteUrl(`/recipes/${recipe.slug}`, siteConfig.url)

  const recipeId = `${recipeUrl}#recipe`
  const webpageId = `${recipeUrl}#webpage`

  const baseRecipeSchema = buildRecipeSchema({
    recipe,
    ratingSummary,
    reviews,
  })

  const recipeSchema = {
    ...baseRecipeSchema,
    "@id": recipeId,
  }

  const breadcrumbSchema = buildBreadcrumbSchema([
    {
      name: "Головна",
      item: absoluteUrl("/", siteConfig.url),
    },
    {
      name: "Рецепти",
      item: absoluteUrl("/recipes", siteConfig.url),
    },
    {
      name: recipe.title,
      item: recipeUrl,
    },
  ])

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": webpageId,

    url: recipeUrl,
    name: recipe.title,
    description: recipe.description,

    inLanguage: siteConfig.locale,

    isPartOf: {
      "@type": "WebSite",
      name: siteConfig.name,
      url: siteConfig.url,
    },

    mainEntity: {
      "@id": recipeId,
    },
  }

  return (
    <>
      <script
        id="recipe-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd(recipeSchema),
        }}
      />
      <script
        id="recipe-webpage-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd(webPageSchema),
        }}
      />
      <script
        id="recipe-breadcrumb-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd(breadcrumbSchema),
        }}
      />
    </>
  )
}